// DX-Comment: DX Comments added in this file to work previous filtered period comparison for smart scalar cards for saved and unsaved questions.
import { isNative } from "metabase/common/utils/card";
import { isEmbedPreview } from "metabase/embedding/config";
import api, { DELETE, GET, POST, PUT } from "metabase/utils/api";
import Question from "metabase-lib/v1/Question";
import { normalizeParameters } from "metabase-lib/v1/parameters/utils/parameter-values";
import { getPivotOptions } from "metabase-lib/v1/queries/utils/pivot";
import dayjs from "dayjs"; // DX-Comment: Added dayjs import to handle date manipulation for previous filtered period comparison.
import _ from "underscore"; // DX-Comment: Added underscore import to use isEqual function for comparing original and shifted filters when calculating previous filtered period dataset query.

export const internalBase = "/api";
export const publicBase = "/api/public";
// use different endpoints for embed previews
export function getEmbedBase() {
  return isEmbedPreview() ? "/api/preview_embed" : "/api/embed";
}

export const ActivityApi = {
  most_recently_viewed_dashboard: GET(
    "/api/activity/most_recently_viewed_dashboard",
  ),
};

// only available with token loaded
export const GTAPApi = {
  list: GET("/api/mt/gtap"),
  attributes: GET("/api/mt/user/attributes"),
  validate: POST("/api/mt/gtap/validate"),
};

export const StoreApi = {
  tokenStatus: GET("/api/premium-features/token/status"),
};

/**
 * Handles API errors for query endpoints. For 4xx errors from streaming query
 * endpoints, the error response body contains the actual error data that should
 * be displayed (just like the old 202-with-error-in-body behavior). This function
 * converts 4xx errors into successful responses with the error data.
 *
 * @param {Promise} apiPromise - The API promise to handle
 * @returns {Promise} The result or error data
 */
async function handleQueryApiError(apiPromise) {
  try {
    return await apiPromise;
  } catch (error) {
    // For 4xx errors, treat the error response body as a successful response
    // (maintaining compatibility with the old 202-with-error-in-body behavior)
    if (
      error &&
      typeof error === "object" &&
      error.status >= 400 &&
      error.status < 500 &&
      error.data
    ) {
      // The QP returns a structured `{ error, error_type, ... }` body, but
      // plainer endpoints (e.g. `/api/embed/*` API-level checks) return a
      // plain-text body. Normalize so callers can rely on a `{ error, ... }`
      // shape and don't fall through to the empty state (EMB-1659).
      if (typeof error.data === "string") {
        return { error: error.data, status: error.status };
      }
      return error.data;
    }
    // For 5xx and other errors, re-throw
    throw error;
  }
}

// Pivot tables need extra data beyond what's described in the MBQL query itself.
// To fetch that extra data we rely on specific APIs for pivot tables that mirrow the normal endpoints.
// Those endpoints take the query along with `pivot_rows` and `pivot_cols` to return the subtotal data.
// If we add breakout/grouping sets to MBQL in the future we can remove this API switching.
export function maybeUsePivotEndpoint(api, card, metadata) {
  const question = new Question(card, metadata);

  // we need to pass pivot_rows, pivot_cols, and totals settings only for ad-hoc queries endpoints
  // in other cases the BE extracts these options from the viz settings
  function wrap(api) {
    return (params, ...rest) => {
      const { pivot_rows, pivot_cols, show_row_totals, show_column_totals } =
        getPivotOptions(question);
      return api(
        {
          ...params,
          pivot_rows,
          pivot_cols,
          show_row_totals,
          show_column_totals,
        },
        ...rest,
      );
    };
  }

  if (
    question.display() !== "pivot" ||
    isNative(card) ||
    // if we have metadata for the db, check if it supports pivots
    (question.database() && !question.database().supportsPivots())
  ) {
    return api;
  }

  const mapping = [
    [MetabaseApi.dataset, MetabaseApi.dataset_pivot, { wrap: true }],
    [CardApi.query, CardApi.query_pivot],
    [DashboardApi.cardQuery, DashboardApi.cardQueryPivot],
    [PublicApi.cardQuery, PublicApi.cardQueryPivot],
    [PublicApi.dashboardCardQuery, PublicApi.dashboardCardQueryPivot],
    [EmbedApi.cardQuery, EmbedApi.cardQueryPivot],
    [EmbedApi.dashboardCardQuery, EmbedApi.dashboardCardQueryPivot],
  ];
  for (const [from, to, options = {}] of mapping) {
    if (api === from) {
      return options.wrap ? wrap(to) : to;
    }
  }
  return api;
}

// DX-Comment: Added function to get the date unit for smart scalar previous filtered period comparison. It looks for the first breakout with a temporal unit and returns that unit. If no temporal unit is found, it defaults to "day".
function getSmartScalarDateUnit(datasetQuery) {
  const breakouts = datasetQuery?.query?.breakout || [];
  for (const breakout of breakouts) {
    if (
      Array.isArray(breakout) &&
      breakout[0] === "field" &&
      breakout.length > 2 &&
      breakout[2] &&
      breakout[2]["temporal-unit"]
    ) {
      return breakout[2]["temporal-unit"];
    }
  }
  return "day";
}

//DX-Comment: Added function to map the date unit to a dayjs unit for date manipulation when calculating the previous filtered period values.
function getDayjsUnit(dateUnit) {
  const unitMap = {
    minute: "minute",
    hour: "hour",
    day: "day",
    week: "week",
    month: "month",
    quarter: "quarter",
    year: "year",
  };

  return Object.keys(unitMap).find((k) => dateUnit.includes(k)) || "day";
}

// DX-Comment: Added function to calculate the previous filtered period value based on the current value and the date unit. It handles both single date values and date ranges, and it subtracts the appropriate amount of time based on the date unit to get the previous period.
function getPreviousFilteredPeriodValue(value, dayjsUnit) {
  if (Array.isArray(value)) {
    return value.map((item) => getPreviousFilteredPeriodValue(item, dayjsUnit));
  }

  if (typeof value !== "string") {
    return value;
  }

  const parts = value.split("~");

  if (parts.length === 2) {
    const startDate = dayjs(parts[0]);
    const endDate = dayjs(parts[1]);

    if (startDate.isValid() && endDate.isValid()) {
      const hasTime = parts[0].includes("T") || parts[1].includes("T");
      const formatStr = hasTime ? undefined : "YYYY-MM-DD";
      const prevStartDate = startDate.subtract(1, dayjsUnit);
      const prevEndDate = endDate.subtract(1, dayjsUnit);

      return `${prevStartDate.format(formatStr)}~${prevEndDate.format(formatStr)}`;
    }
  } else if (parts.length === 1) {
    const date = dayjs(parts[0]);

    if (date.isValid()) {
      const hasTime = parts[0].includes("T");
      const formatStr = hasTime ? undefined : "YYYY-MM-DD";
      const prevDate = date.subtract(1, dayjsUnit);

      return prevDate.format(formatStr);
    }
  }

  return value;
}

// DX-Comment: Added function to get previous filtered period parameters.
function getPreviousFilteredPeriodParameters(parameters, dayjsUnit) {
  return (parameters || []).map((param) => {
    if (param?.type?.startsWith("date/") && param.value != null) {
      return {
        ...param,
        value: getPreviousFilteredPeriodValue(param.value, dayjsUnit),
      };
    }

    return param;
  });
}

// DX-Comment: Added function to shift the date values in the dataset query filter for calculating the previous filtered period dataset query. It recursively traverses the filter object and shifts any date values it finds based on the date unit.
function shiftDatasetQueryDateValue(value, dayjsUnit) {
  if (Array.isArray(value)) {
    return value.map((item) => shiftDatasetQueryDateValue(item, dayjsUnit));
  }

  if (typeof value !== "string") {
    return value;
  }

  const parts = value.split("~");
  if (parts.length === 2) {
    const startDate = dayjs(parts[0]);
    const endDate = dayjs(parts[1]);

    if (startDate.isValid() && endDate.isValid()) {
      const hasTime = parts[0].includes("T") || parts[1].includes("T");
      const formatStr = hasTime ? undefined : "YYYY-MM-DD";
      const prevStartDate = startDate.subtract(1, dayjsUnit);
      const prevEndDate = endDate.subtract(1, dayjsUnit);
      return `${prevStartDate.format(formatStr)}~${prevEndDate.format(formatStr)}`;
    }
  } else {
    const date = dayjs(value);
    if (date.isValid()) {
      const hasTime = value.includes("T");
      const formatStr = hasTime ? undefined : "YYYY-MM-DD";
      return date.subtract(1, dayjsUnit).format(formatStr);
    }
  }

  return value;
}

// DX-Comment: Added function to shift the date values in the dataset query filter for calculating the previous filtered period dataset query. It recursively traverses the filter object and shifts any date values it finds based on the date unit.
function shiftDatasetQueryFilter(filter, dayjsUnit) {
  if (Array.isArray(filter)) {
    return filter.map((item, index) =>
      index === 0 ? item : shiftDatasetQueryFilter(item, dayjsUnit),
    );
  }

  if (filter && typeof filter === "object") {
    return Object.fromEntries(
      Object.entries(filter).map(([key, value]) => [
        key,
        shiftDatasetQueryFilter(value, dayjsUnit),
      ]),
    );
  }

  return shiftDatasetQueryDateValue(filter, dayjsUnit);
}

// DX-Comment: Added function to get the previous filtered period dataset query by shifting the date values in the filter of the original dataset query. It checks if there is a filter in the dataset query, and if so, it shifts the date values in the filter using the shiftDatasetQueryFilter function. If the shifted filter is the same as the original filter, it returns null to indicate that there is no valid previous period query. Otherwise, it returns a new dataset query object with the shifted filter.
function getPreviousFilteredPeriodDatasetQuery(question, dayjsUnit) {
  const datasetQuery = question.datasetQuery();
  const filter = datasetQuery?.query?.filter;
  if (!filter) {
    return null;
  }

  const shiftedFilter = shiftDatasetQueryFilter(filter, dayjsUnit);
  if (_.isEqual(shiftedFilter, filter)) {
    return null;
  }

  return {
    ...datasetQuery,
    query: {
      ...datasetQuery.query,
      filter: shiftedFilter,
    },
  };
}

/**
 * @param {*} question
 * @param {object} param
 */
export async function runQuestionQuery(
  question,
  {
    cancelDeferred,
    isDirty = false,
    token,
    ignoreCache = false,
    collectionPreview = false,
    // Ability to override or add extra query params to the request, used by Embedding SDK
    queryParamsOverride = {},
  } = {},
) {
  const canUseCardApiEndpoint = !isDirty && question.isSaved();
  const parameters = normalizeParameters(
    question.parameters({ collectionPreview }),
  );
  const card = question.card();
  // DX-Comment: Added isSmartScalar and hasPreviousFilteredPeriodComparison constants.
  const isSmartScalar =
      question.display() === "smartscalar" ||
      question.settings()?.display === "smartscalar";
  const hasPreviousFilteredPeriodComparison =
      question.settings()?.["scalar.comparisons"]?.some(
        (comparison) => comparison?.type === "previousFilteredPeriod",
      );

  if (canUseCardApiEndpoint) {
    const { dashboardId, dashcardId } = question.getDashboardProps();

    const queryParams = {
      ...(token ? { token } : { cardId: question.id() }),
      dashboardId,
      dashcardId,
      ignore_cache: ignoreCache,
      collection_preview: collectionPreview,
      parameters,
      ...queryParamsOverride,
    };
    // DX-Comment: Added cancelOptions to pass the cancellation promise to the API call if cancelDeferred is provided.
    const cancelOptions = cancelDeferred
      ? { cancelled: cancelDeferred.promise }
      : {};
    const endpoint = dashboardId ? DashboardApi.cardQuery : CardApi.query;

    // DX-Comment: Added complete below logic to handle previous filtered period comparison for smart scalar cards when using the Card API endpoint. If the card has a previous filtered period comparison, we need to make an additional query to fetch the data for the previous period and include it in the result. We check if there are parameters to determine how to calculate the previous period parameters, and then we make the necessary API calls to get both the current and previous period results. Finally, we combine the results by adding the previous period data to the current result before returning it.
    const currentResult = await maybeUsePivotEndpoint(
      endpoint,
      card,
      question.metadata(),
    )(queryParams, cancelOptions);

    if (isSmartScalar && hasPreviousFilteredPeriodComparison) {
      const dateUnit = getSmartScalarDateUnit(question.datasetQuery());
      const dayjsUnit = getDayjsUnit(dateUnit);
      let prevResult;
      let prevQueryParams;
      let prevDatasetQuery;

      if (Array.isArray(parameters) && parameters.length > 0) {
        const prevParameters = getPreviousFilteredPeriodParameters(
          parameters,
          dayjsUnit,
        );

        prevQueryParams = {
          ...queryParams,
          parameters: prevParameters,
        };

        prevResult = await maybeUsePivotEndpoint(
          endpoint,
          card,
          question.metadata(),
        )(prevQueryParams, cancelOptions);
      } else {
        prevDatasetQuery = getPreviousFilteredPeriodDatasetQuery(
          question,
          dayjsUnit,
        );
        if (prevDatasetQuery) {
          prevResult = await maybeUsePivotEndpoint(
            MetabaseApi.dataset,
            card,
            question.metadata(),
          )(prevDatasetQuery, cancelOptions);
        }
      }
    }

    return [currentResult];
  }

  // DX-Comment: Added queryparameters as a parameter to function.
  const getDatasetQueryResult = (datasetQuery, queryParameters = parameters) => {
    const datasetQueryWithParameters = { ...datasetQuery, parameters: queryParameters };
    return handleQueryApiError(
      maybeUsePivotEndpoint(
        MetabaseApi.dataset,
        card,
        question.metadata(),
      )(
        datasetQueryWithParameters,
        cancelDeferred
          ? {
              cancelled: cancelDeferred.promise,
            }
          : {},
      ),
    );
  };

   // DX-Comment: Added if block to reflect data for unsaved questions when they have previous filtered period comparison for smart scalar.
  if (isSmartScalar && hasPreviousFilteredPeriodComparison) {
    const currentResult = await getDatasetQueryResult(question.datasetQuery());
    const dateUnit = getSmartScalarDateUnit(question.datasetQuery());
    const dayjsUnit = getDayjsUnit(dateUnit);
    let prevResult;

    if (Array.isArray(parameters) && parameters.length > 0) {
      const prevParameters = getPreviousFilteredPeriodParameters(
        parameters,
        dayjsUnit,
      );
      const prevDatasetQuery = question.datasetQuery();
      prevResult = await getDatasetQueryResult(prevDatasetQuery, prevParameters);
    } else {
      const prevDatasetQuery = getPreviousFilteredPeriodDatasetQuery(
        question,
        dayjsUnit,
      );
      if (prevDatasetQuery) {
        prevResult = await maybeUsePivotEndpoint(
          MetabaseApi.dataset,
          card,
          question.metadata(),
        )(
          prevDatasetQuery,
          cancelDeferred
            ? {
                cancelled: cancelDeferred.promise,
              }
            : {},
        );
      }
    }

    if (prevResult?.data && currentResult?.data) {
      currentResult.data = {
        ...currentResult.data,
        previous_filtered_period_data: prevResult.data,
      };
    }

    return [currentResult];
  }

  return [await getDatasetQueryResult(question.datasetQuery())]; // DX-Comment: Updated to use getDatasetQueryResult function which allows passing query parameters for unsaved questions and handles previous filtered period comparison for smart scalar cards.
}

export const CardApi = {
  get: GET("/api/card/:cardId"),
  update: PUT("/api/card/:id"),
  query: POST("/api/card/:cardId/query"),
  query_pivot: POST("/api/card/pivot/:cardId/query"),
  // related
  compatibleCards: GET("/api/card/:cardId/series"),
  parameterValues: GET("/api/card/:cardId/params/:paramId/values"),
  parameterSearch: GET("/api/card/:cardId/params/:paramId/search/:query"),
};

export const DashboardApi = {
  get: GET("/api/dashboard/:dashId"),
  parameterValues: GET("/api/dashboard/:dashId/params/:paramId/values"),
  parameterSearch: GET("/api/dashboard/:dashId/params/:paramId/search/:query"),
  validFilterFields: GET("/api/dashboard/params/valid-filter-fields"),
  cardQuery: POST(
    "/api/dashboard/:dashboardId/dashcard/:dashcardId/card/:cardId/query",
  ),
  cardQueryPivot: POST(
    "/api/dashboard/pivot/:dashboardId/dashcard/:dashcardId/card/:cardId/query",
  ),
};

export const CollectionsApi = {
  get: GET("/api/collection/:id"),
  graph: GET("/api/collection/graph"),
  updateGraph: PUT("/api/collection/graph?skip-graph=true"),
};

const PIVOT_PUBLIC_PREFIX = `${publicBase}/pivot/`;

export const PublicApi = {
  action: GET(`${publicBase}/action/:uuid`),
  executeDashcardAction: POST(
    `${publicBase}/dashboard/:dashboardId/dashcard/:dashcardId/execute`,
  ),
  executeAction: POST(`${publicBase}/action/:uuid/execute`),
  card: GET(`${publicBase}/card/:uuid`),
  cardQuery: GET(`${publicBase}/card/:uuid/query`),
  cardQueryPivot: GET(PIVOT_PUBLIC_PREFIX + "card/:uuid/query"),
  dashboard: GET(`${publicBase}/dashboard/:uuid`),
  dashboardCardQuery: GET(
    `${publicBase}/dashboard/:uuid/dashcard/:dashcardId/card/:cardId`,
  ),
  dashboardCardQueryPivot: GET(
    PIVOT_PUBLIC_PREFIX + "dashboard/:uuid/dashcard/:dashcardId/card/:cardId",
  ),
  prefetchDashcardValues: GET(
    `${publicBase}/dashboard/:dashboardId/dashcard/:dashcardId/execute`,
  ),
  document: GET(`/api/public/document/:uuid`),
  documentCardQuery: GET(`/api/public/document/:uuid/card/:cardId`),
};

export const EmbedApi = {
  card: GET(getEmbedBase() + "/card/:token"),
  cardQuery: GET(getEmbedBase() + "/card/:token/query"),
  cardQueryPivot: GET(getEmbedBase() + "/pivot/card/:token/query"),
  dashboard: GET(getEmbedBase() + "/dashboard/:token"),
  dashboardCardQuery: GET(
    getEmbedBase() + "/dashboard/:token/dashcard/:dashcardId/card/:cardId",
  ),
  dashboardCardQueryPivot: GET(
    getEmbedBase() +
      "/pivot/dashboard/:token/dashcard/:dashcardId/card/:cardId",
  ),
};

export const AutoApi = {
  dashboard: GET("/api/automagic-dashboards/:subPath", {
    // this prevents the `subPath` parameter from being URL encoded
    raw: { subPath: true },
  }),
};

export const MetabaseApi = {
  db_usage_info: GET("/api/database/:dbId/usage_info"),
  tableAppendCSV: POST("/api/table/:tableId/append-csv", {
    formData: true,
    fetch: true,
  }),
  tableReplaceCSV: POST("/api/table/:tableId/replace-csv", {
    formData: true,
    fetch: true,
  }),
  dataset: POST("/api/dataset"),
  dataset_pivot: POST("/api/dataset/pivot"),

  // to support audit app  allow the endpoint to be provided in the query
  datasetEndpoint: POST("/api/:endpoint", {
    // this prevents the `endpoint` parameter from being URL encoded
    raw: { endpoint: true },
  }),
};

export const ParameterApi = {
  parameterValues: POST("/api/dataset/parameter/values"),
  parameterSearch: POST("/api/dataset/parameter/search/:query"),
};

export const ModerationReviewApi = {
  create: POST("/api/moderation-review"),
  update: PUT("/api/moderation-review/:id"),
};

export const PulseApi = {
  list: GET("/api/pulse"),
  create: POST("/api/pulse"),
  get: GET("/api/pulse/:pulseId"),
  update: PUT("/api/pulse/:id"),
  test: POST("/api/pulse/test"),
  form_input: GET("/api/pulse/form_input"),
  unsubscribe: DELETE("/api/pulse/:id/subscription"),
};

/// this in unauthenticated, for letting people who are not logged in unsubscribe from Alerts/DashboardSubscriptions
export const PulseUnsubscribeApi = {
  unsubscribe: POST("/api/pulse/unsubscribe"),
  undo_unsubscribe: POST("/api/pulse/unsubscribe/undo"),
};

// also unauthenticated
export const NotificationUnsubscribeApi = {
  unsubscribe: POST("/api/notification/unsubscribe"),
  undo_unsubscribe: POST("/api/notification/unsubscribe/undo"),
};

export const RevisionsApi = {
  get: GET("/api/revision/:entity/:id"),
};

export const SessionApi = {
  create: POST("/api/session"),
  createWithGoogleAuth: POST("/api/session/google_auth"),
  delete: DELETE("/api/session"),
  slo: POST("/auth/sso/logout"),
  forgot_password: POST("/api/session/forgot_password"),
  reset_password: POST("/api/session/reset_password"),
};

export const SettingsApi = {
  list: GET("/api/setting"),
  put: PUT("/api/setting/:key"),
  putAll: PUT("/api/setting"),
};

export const PermissionsApi = {
  graph: GET("/api/permissions/graph"),
  graphForGroup: GET("/api/permissions/graph/group/:groupId"),
  graphForDB: GET("/api/permissions/graph/db/:databaseId"),
  updateGraph: PUT("/api/permissions/graph"),
};

export const PersistedModelsApi = {
  enablePersistence: POST("/api/persist/enable"),
  disablePersistence: POST("/api/persist/disable"),
  setRefreshSchedule: POST("/api/persist/set-refresh-schedule"),
};

export const SetupApi = {
  create: POST("/api/setup"),
};

export const UserApi = {
  list: GET("/api/user/recipients"),
  current: GET("/api/user/current"),
  update_qbnewb: PUT("/api/user/:id/modal/qbnewb"),
};

// TODO: move to all functions to RTK (metabase/api/util.ts)
export const UtilApi = {
  password_check: POST("/api/session/password-check"),
  random_token: GET("/api/util/random_token"),
  logs: GET("/api/logger/logs"),
  bug_report_details: GET("/api/bug-reporting/details"),
  get_connection_pool_details_url: () => {
    // this one does not need an HTTP verb because it's opened as an external link
    // and it can be deployed at subpath
    const path = "/api/bug-reporting/connection-pool-details";
    const { href } = new URL(api.basename + path, location.origin);

    return href;
  },
};

export const FrontendErrorsApi = {
  report: POST("/api/frontend-errors"),
};

export const ActionsApi = {
  execute: POST("/api/action/:id/execute"),
  prefetchValues: GET("/api/action/:id/execute"),
  prefetchDashcardValues: GET(
    "/api/dashboard/:dashboardId/dashcard/:dashcardId/execute",
  ),
  executeDashcardAction: POST(
    "/api/dashboard/:dashboardId/dashcard/:dashcardId/execute",
  ),
};
